/**
 * PC-v2.2-C1 — GRÁFICOS DEL PORTAL (columnas, barra apilada y barras comparadas)
 *
 * HTML y CSS, sin librerías: el texto conserva su tamaño en móvil (un SVG escalado lo encogería) y no se añade ninguna
 * dependencia. Pintan cifras que ya llegan calculadas del servidor; aquí no se calcula nada que no sea una
 * proporción para dibujar.
 *
 * ACCESIBILIDAD
 *   · Cada serie se distingue por color Y por trama (lisa, rayas, puntos): no depende sólo del color.
 *   · El dibujo es decorativo para el lector de pantalla (`aria-hidden`) y su resumen va en `aria-label`; los valores
 *     exactos están siempre en «Ver como tabla», que es una tabla HTML normal.
 *   · Un tramo sin datos (antes del primer registro) se dibuja rayado en gris y dice «Sin datos»: nunca como un 0.
 */
import { useId, useState, type ReactNode } from 'react';

export type Trama = 'lisa' | 'rayas' | 'inversa' | 'puntos' | 'cuadros' | 'tenue';

export interface SerieGrafico {
  readonly clave: string;
  readonly nombre: string;
  /** Un valor por categoría; null = sin datos en esa categoría. */
  readonly valores: readonly (number | null)[];
  readonly color: string;
  readonly trama: Trama;
}

export interface CategoriaGrafico {
  readonly clave: string;
  /** Etiqueta bajo la columna (corta: cabe en móvil). */
  readonly etiqueta: string;
  /** Etiqueta completa (tabla y título). */
  readonly larga: string;
}

export function Muestra({ color, trama }: { color: string; trama: Trama }): ReactNode {
  return <span aria-hidden="true" className={`grafico-muestra trama-${trama}`} style={{ ['--c' as string]: color }} />;
}

function Leyenda({ items }: { items: readonly { clave: string; nombre: string; color: string; trama: Trama }[] }): ReactNode {
  return (
    <ul className="grafico-leyenda">
      {items.map((s) => <li key={s.clave}><Muestra color={s.color} trama={s.trama} /> {s.nombre}</li>)}
    </ul>
  );
}

/** Columnas agrupadas: una o varias series por categoría. `maximo` fija la escala (100 en porcentajes). */
export function GraficoColumnas({ etiqueta, categorias, series, maximo, formato = String, conSinDatos }: {
  etiqueta: string;
  categorias: readonly CategoriaGrafico[];
  series: readonly SerieGrafico[];
  maximo?: number;
  formato?: (v: number) => string;
  /** Hay categorías sin datos: se explica en la leyenda. */
  conSinDatos: boolean;
}): ReactNode {
  const max = maximo ?? Math.max(1, ...series.flatMap((s) => s.valores.map((v) => v ?? 0)));
  const conValores = categorias.length <= 8;
  //  Con muchas categorías, una etiqueta de cada `paso` (unas seis): en móvil no caben todas sin partirse.
  const paso = conValores ? 1 : Math.ceil(categorias.length / 6);
  return (
    <div className="grafico">
      <div className="grafico-escala pequena tenue" aria-hidden="true">{formato(max)}</div>
      <div className="grafico-columnas" role="img" aria-label={etiqueta}>
        {categorias.map((c, k) => {
          const sinDatos = series.every((s) => s.valores[k] === null);
          return (
            <div key={c.clave} className="grafico-cat" title={c.larga}>
              <div className={`grafico-cat-barras${sinDatos ? ' grafico-sin-datos' : ''}`} aria-hidden="true">
                {!sinDatos && series.map((s) => {
                  const v = s.valores[k] ?? null;
                  return (
                    <div key={s.clave} className="grafico-col">
                      {conValores && <span className="grafico-valor">{v === null ? '—' : formato(v)}</span>}
                      <span
                        className={`grafico-barra trama-${s.trama}${v === null ? ' grafico-barra-nula' : ''}`}
                        style={{ ['--c' as string]: s.color, height: `${String(v === null ? 0 : Math.max((v / max) * 100, v > 0 ? 2 : 0))}%` }}
                      />
                    </div>
                  );
                })}
                {sinDatos && <span className="grafico-sin-datos-texto">Sin datos</span>}
              </div>
              <span className={`grafico-etq${conValores ? '' : ' grafico-etq-corta'}`} aria-hidden="true">{k % paso === 0 ? c.etiqueta : ''}</span>
            </div>
          );
        })}
      </div>
      <Leyenda items={[
        ...series.map((s) => ({ clave: s.clave, nombre: s.nombre, color: s.color, trama: s.trama })),
        ...(conSinDatos ? [{ clave: 'sin-datos', nombre: 'Sin datos (antes del primer registro)', color: '#94a3b8', trama: 'tenue' as const }] : []),
      ]} />
    </div>
  );
}

export interface SegmentoApilado {
  readonly clave: string;
  readonly nombre: string;
  readonly valor: number;
  readonly color: string;
  readonly trama: Trama;
}

/** Una barra con segmentos EXCLUYENTES que suman `total` (G5). */
export function BarraApilada({ etiqueta, segmentos, total }: { etiqueta: string; segmentos: readonly SegmentoApilado[]; total: number }): ReactNode {
  const pct = (v: number): string => (total === 0 ? '0' : String(Math.round((v * 1000) / total) / 10).replace('.', ','));
  return (
    <div className="grafico">
      {total === 0 ? <p className="pequena tenue" style={{ margin: 0 }}>No hay órdenes de trabajo en este periodo.</p> : (
        <div className="grafico-apilada" role="img" aria-label={etiqueta}>
          {segmentos.filter((s) => s.valor > 0).map((s) => (
            <span key={s.clave} className={`grafico-segmento trama-${s.trama}`} style={{ ['--c' as string]: s.color, flexGrow: s.valor }}
              title={`${s.nombre}: ${String(s.valor)}`} aria-hidden="true" />
          ))}
        </div>
      )}
      <ul className="grafico-leyenda grafico-leyenda-valores">
        {segmentos.map((s) => (
          <li key={s.clave} data-segmento={s.clave}>
            <Muestra color={s.color} trama={s.trama} /> {s.nombre}: <strong>{s.valor}</strong>
            {total > 0 && <span className="tenue"> ({pct(s.valor)} %)</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface FilaComparada {
  readonly clave: string;
  readonly nombre: string;
  readonly valores: readonly number[];
}

/** Barras horizontales comparadas: una fila por elemento, una barra por serie, misma escala para todas (G6). */
export function BarrasComparadas({ etiqueta, filas, series }: {
  etiqueta: string;
  filas: readonly FilaComparada[];
  series: readonly { clave: string; nombre: string; color: string; trama: Trama }[];
}): ReactNode {
  const max = Math.max(1, ...filas.flatMap((f) => f.valores));
  return (
    <div className="grafico">
      <ul className="grafico-comparadas" role="img" aria-label={etiqueta}>
        {filas.map((f) => (
          <li key={f.clave} aria-hidden="true">
            <span className="grafico-comparadas-nombre">{f.nombre}</span>
            {series.map((s, k) => (
              <span key={s.clave} className="grafico-comparadas-fila">
                <span className="grafico-comparadas-fondo">
                  <span className={`grafico-comparadas-barra trama-${s.trama}`}
                    style={{ ['--c' as string]: s.color, width: `${String(((f.valores[k] ?? 0) / max) * 100)}%` }} />
                </span>
                <span className="grafico-comparadas-valor mono">{f.valores[k] ?? 0}</span>
              </span>
            ))}
          </li>
        ))}
      </ul>
      <Leyenda items={series} />
    </div>
  );
}

/**
 * El marco de cada gráfico: título, explicación, el dibujo y «Ver como tabla» (los valores exactos). La tabla se
 * despliega en el sitio; el dibujo no se quita, para no mover la pantalla.
 */
export function MarcoGrafico({ titulo, explicacion, nota, tabla, children }: {
  titulo: string;
  explicacion: ReactNode;
  nota?: ReactNode;
  tabla: ReactNode;
  children: ReactNode;
}): ReactNode {
  const id = useId();
  const [conTabla, setConTabla] = useState(false);
  return (
    <section className="panel grafico-marco" aria-labelledby={`${id}-t`}>
      <div className="panel-cabecera"><h3 id={`${id}-t`} style={{ margin: 0 }}>{titulo}</h3></div>
      <div className="panel-cuerpo">
        <p className="pequena tenue" style={{ marginTop: 0 }}>{explicacion}</p>
        {children}
        {nota !== undefined && <p className="pequena aviso" style={{ marginBottom: 0 }}>{nota}</p>}
        <button type="button" className="enlace pequena" aria-expanded={conTabla} aria-controls={`${id}-tabla`} onClick={() => setConTabla((v) => !v)}>
          {conTabla ? 'Ocultar la tabla' : 'Ver como tabla'}
        </button>
        {conTabla && <div id={`${id}-tabla`} className="tabla-desliza">{tabla}</div>}
      </div>
    </section>
  );
}
